import React, { useState, useEffect, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { spacing, themeLight, radius } from '../../../theme/index';
import { Text, TextVariant } from '../../../text/index';
import { Check, Remove } from '@styled-icons/material';
export const Checkbox = forwardRef(({ checked = false, onChange, name, label, labelSize = TextVariant.ArticleText, message, error = false, disabled = false, indeterminate = false, className, ...rest }, ref) => {
    const [isChecked, setIsChecked] = useState(checked);
    const [mouseDown, setMouseDown] = useState(false);
    useEffect(() => {
        setIsChecked(checked);
    }, [checked]);
    const handleChange = () => {
        const nextChecked = !isChecked;
        setIsChecked(nextChecked);
        if (onChange) {
            onChange({ name: name, value: nextChecked });
        }
    };
    const removeOutline = () => {
        setMouseDown(true);
    };
    const addOutline = (e) => {
        if (e.key === 'Tab') {
            setMouseDown(false);
        }
    };
    useEffect(() => {
        document.addEventListener('mousedown', removeOutline);
        return () => document.removeEventListener('mousedown', removeOutline);
    }, []);
    useEffect(() => {
        document.addEventListener('keydown', addOutline);
        return () => document.removeEventListener('keydown', addOutline);
    }, []);
    return (<Container className={className} id={mouseDown ? 'mousedown' : ''}>
        <CheckboxWrap disabled={disabled}>
          <ButtonWrap>
            <PseudoCheckbox name={name} checked={isChecked} error={error} disabled={disabled} onClick={handleChange} aria-label="Checkbox" ref={ref} type="button" {...rest}>
              {indeterminate ? <Remove /> : <Check />}
            </PseudoCheckbox>
          </ButtonWrap>
          <Label variant={labelSize} disabled={disabled} onClick={!disabled ? handleChange : null}>
            {label}
          </Label>
        </CheckboxWrap>
        {message && (<Message variant={TextVariant.Small} error={error}>
            {message}
          </Message>)}
      </Container>);
});
export default Checkbox;
const Container = styled.div `
  display: inline-flex;
  flex-direction: column;

  &#mousedown *:focus {
    outline: 0;
  }
`;
const ButtonWrap = styled.div `
  display: inline-flex;
  width: fit-content;
  align-items: center;
`;
const PseudoCheckbox = styled.button `
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 0;
  min-width: ${spacing.space20};
  width: ${spacing.space20};
  height: ${spacing.space20};
  border-radius: ${radius.s};
  box-sizing: border-box;
  transition: background 0.2 ease;
  border: 2px solid;
  border-color: ${({ theme }) => (theme.border ? theme.border.heavy : themeLight.border.heavy)};
  background: ${({ theme }) => (theme.background ? theme.background.default : themeLight.background.default)};
  color: ${({ theme }) => (theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText)};

  svg {
    position: absolute;
    display: ${({ checked }) => (checked ? 'initial' : 'none')};
    font-size: ${spacing.space20};
  }

  &:hover {
    cursor: pointer;
  }

  ${({ checked }) => checked &&
    css `
      border-color: ${({ theme }) => theme.componentColors
        ? theme.componentColors.interactiveElement.active
        : themeLight.componentColors.interactiveElement.active};
      background: ${({ theme }) => theme.componentColors
        ? theme.componentColors.interactiveElement.active
        : themeLight.componentColors.interactiveElement.active};
    `}

  ${({ error }) => error &&
    css `
      border-color: ${({ theme }) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error};
      background: ${({ theme }) => (theme.background ? theme.background.default : themeLight.background.default)};
      color: ${({ theme }) => theme.componentColors
        ? theme.componentColors.alert.errorBackground
        : themeLight.componentColors.alert.errorBackground};
    `}

  ${({ error, checked }) => error &&
    checked &&
    css `
      border-color: ${({ theme }) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error};
      background: ${({ theme }) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error};
    `}

  ${({ disabled }) => disabled &&
    css `
      border-color: ${({ theme }) => (theme.border ? theme.border.default : themeLight.border.default)};
      background: ${({ theme }) => (theme.background ? theme.background.default : themeLight.background.default)};
      color: ${({ theme }) => (theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText)};

      &:hover {
        cursor: not-allowed;
        border-color: ${({ theme }) => (theme.border ? theme.border.default : themeLight.border.default)};
      }
    `}

  ${({ disabled, checked }) => disabled &&
    checked &&
    css `
      border-color: ${({ theme }) => (theme.border ? theme.border.default : themeLight.border.default)};
      background: ${({ theme }) => theme.componentColors
        ? theme.componentColors.interactiveElement.disabled
        : themeLight.componentColors.interactiveElement.disabled};
    `}
`;
const Label = styled(Text) `
  padding-left: ${spacing.space8};
  margin: 0;
  color: ${({ theme }) => (theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText)};

  &:hover {
    cursor: pointer;
  }

  ${({ disabled }) => disabled &&
    css `
      color: ${({ theme }) => (theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText)};

      &:hover {
        cursor: not-allowed;
      }
    `}
`;
const Message = styled(Text) `
  margin: 0;
  margin-left: calc(${spacing.space20} + ${spacing.space8});

  color: ${({ theme }) => (theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText)};

  ${({ error }) => error &&
    css `
      color: ${({ theme }) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error};
    `}
`;
const CheckboxWrap = styled.div `
  display: inline-flex;
  align-items: center;
  width: fit-content;
  height: fit-content;

  ${({ disabled }) => !disabled &&
    css `
      &:hover {
        ${PseudoCheckbox} {
          border-color: ${({ theme }) => (theme.border ? theme.border.default : themeLight.border.default)};
        }
      }
    `}
`;
